<template>
  <payments-section v-if="isAdmin || manageUrl || hasAnyPasses || purchases.length > 0" :title="title" :count="loading || flat ? null : sources.length" :flat="flat" :actions-when-closed="!!manageUrl">
    <!-- The parent page's family view is read-only; passes are added and used on the wrestler's page -->
    <template v-if="manageUrl" v-slot:actions>
      <a :href="manageUrl" class="btn btn-default btn-compact">Add or use passes</a>
    </template>
    <div class="wp-section-subtitle">Each check-in uses one.<span v-if="isAdmin"> Admins can add or use them by hand.</span></div>
    <div v-if="isAdmin" class="wp-pass-manager">
      <class-pass-manager
        :show-stats="false"
        :available-count="availableCount"
        :used-count="usedCount"
        :wrestler-id="wrestlerId"
        :wrestler-name="wrestlerName"
        :admin-name="adminName"
      ></class-pass-manager>
    </div>

    <spinner v-if="loading"></spinner>
    <template v-else>
      <div v-if="sources.length === 0" class="wp-section-subtitle m-t-md">No passes available.</div>
      <!-- One row per source (a drop in / trial purchase, a subscription's weekly
           allotment, coach-added passes), count first so the numbers line up.
           Used-up purchases stay, dimmed, so a used trial is still on record. -->
      <!-- divs, not ul/li: the app embed restyles every li (and p) as iOS body text -->
      <div v-else class="wp-balance" role="list">
        <div v-for="source in sources" :key="source.key" class="wp-balance__item" :class="{ 'wp-balance__item--done': source.count === 0 }" role="listitem">
          <span class="wp-balance__count">{{ source.count }}<span v-if="source.of" class="wp-balance__of"> of {{ source.of }}</span></span>
          <div class="wp-balance__main">
            <div class="wp-balance__name">
              {{ source.name }}
              <span v-if="source.badge" class="charge-badge" :class="`charge-badge--${source.badge.tone}`">{{ source.badge.text }}</span>
            </div>
            <div class="wp-muted">{{ source.detail }}</div>
            <div v-if="source.purchase" class="wp-balance__purchase">{{ source.purchase }}</div>
          </div>
        </div>
      </div>

      <template v-if="activity.length > 0">
        <button type="button" class="wp-link-button m-t-md" @click="showActivity = !showActivity">
          {{ activityLabel }}
        </button>
        <div v-if="showActivity" class="wp-rows" role="list">
          <div v-for="row in activity" :key="row.id" role="listitem">
            <div class="wp-row__main">
              <span class="font-semi" :class="{ 'wp-muted': row.muted }">{{ row.title }}</span>
              <span class="wp-muted"> · {{ row.detail }}</span>
            </div>
            <span class="wp-row__aside">{{ row.date }} · {{ row.source }}</span>
          </div>
        </div>
        <button v-if="showActivity && nextActivityUrl" type="button" class="wp-link-button wp-link-button--primary m-t-xs" @click="loadActivity(nextActivityUrl)">
          Show more
        </button>
      </template>
    </template>
    <p v-if="error" class="text-danger m-t-sm">{{ errorMessage }}</p>
  </payments-section>
</template>
<script>
import Spinner from './spinner.vue';
import ClassPassManager from './class_pass_manager.vue';
import PaymentsSection from './payments_section.vue';
import { formattingMixin } from './mixins/formatting_mix';

// Class passes are attendance credits rather than payments: show what's
// available grouped by where it came from (a drop in / trial session, or a
// class-limited subscription's weekly allotment), the admin Use / Add
// actions, and a plain-language history of each pass.
export default {
  name: 'wrestler-class-passes',
  components: { Spinner, ClassPassManager, PaymentsSection },
  mixins: [formattingMixin],
  props: {
    title: { type: String, default: 'Class passes' },
    wrestlerId: { type: Number, required: true },
    wrestlerName: { type: String, required: true },
    adminName: { type: String, default: '' },
    isAdmin: { type: Boolean, default: false },
    subscriptions: { type: Array, default: () => [] },
    // Where an admin adds or uses this wrestler's passes, when they can't here
    manageUrl: { type: String, default: null },
    // A sub-heading inside a kid's section (parent / coach page) rather than its own section
    flat: { type: Boolean, default: false },
    // This wrestler's drop in / trial registrations (expand[]=class_passes
    // gives the pass counts each purchase issued)
    purchases: { type: Array, default: () => [] },
    // The wrestler themself is viewing: no amounts, payer or Overdue badge
    hidePayments: { type: Boolean, default: false },
    availableCount: { type: Number, default: 0 },
    usedCount: { type: Number, default: 0 },
  },
  data() {
    return {
      loading: true,
      error: false,
      errorMessage: '',
      available: [],
      passes: [],
      nextActivityUrl: null,
      showActivity: false,
      usedTotal: 0,
      expiredTotal: 0,
    };
  },
  computed: {
    // "Show 6 used passes" rather than a bare "Show pass activity"
    activityLabel() {
      if (this.showActivity) {
        return 'Hide pass activity';
      }
      const parts = [];
      if (this.usedTotal > 0) {
        parts.push(`${this.usedTotal} used`);
      }
      if (this.expiredTotal > 0) {
        parts.push(`${this.expiredTotal} expired`);
      }
      if (parts.length === 0) {
        return 'Show pass activity';
      }
      return `Show ${parts.join(' and ')} ${this.usedTotal + this.expiredTotal === 1 ? 'pass' : 'passes'}`;
    },
    hasAnyPasses() {
      return this.loading || this.passes.length > 0;
    },
    passesLeft() {
      return this.loading ? null : _.sumBy(this.sources, 'count');
    },
    sources() {
      const purchased = Object.values(_.groupBy(this.purchases, (r) => r.paid_session.id))
        .map((registrations) => this.purchaseSource(_.orderBy(registrations, 'created_at', 'desc')));
      // Weekly allotments and coach-added passes; purchased passes are counted above
      const groups = _.groupBy(this.available.filter((pass) => !pass.registration_id), (pass) => this.sourceKey(pass));
      const other = Object.entries(groups).map(([key, passes]) => {
        const sample = passes[0];
        const soonest = _.min(_.compact(passes.map((p) => p.expires_at)));
        let detail;
        if (sample.billing_subscription) {
          detail = soonest ? `This week · resets ${moment(soonest).format('ddd MMM D')}` : 'This week';
        } else {
          detail = this.expiryText(passes);
        }
        return {
          key,
          name: this.sourceName(sample),
          count: passes.length,
          of: sample.billing_subscription ? this.weeklyQuantity(sample.billing_subscription.id) : null,
          detail,
          purchase: sample.billing_subscription ? null : 'Added by a coach',
          sortAt: _.max(passes.map((p) => p.created_at)),
        };
      });
      // Passes left first, then used-up purchases, newest first
      return _.orderBy([...purchased, ...other], [(row) => (row.count > 0 ? 0 : 1), 'sortAt'], ['asc', 'desc']);
    },
    activity() {
      return this.passes.map((pass) => {
        let title;
        let detail;
        let date;
        let muted = false;
        if (pass.status === 'used' && pass.check_in) {
          title = 'Used';
          detail = pass.check_in.event_name || 'Check-in';
          date = pass.check_in.created_at;
        } else if (pass.status === 'used') {
          title = 'Used';
          detail = 'marked used by an admin';
          date = pass.updated_at;
        } else if (this.isExpired(pass)) {
          title = 'Expired unused';
          detail = 'never checked in';
          date = pass.expires_at || pass.updated_at;
          muted = true;
        } else {
          title = 'Available';
          detail = pass.expires_at ? `expires ${this.shortDate(pass.expires_at)}` : 'no expiry';
          date = pass.created_at;
        }
        return {
          id: pass.id, title, detail, muted, date: this.shortDate(date), source: this.sourceName(pass),
        };
      });
    },
  },
  watch: {
    // The kid's section on a parent / coach page says how many are left
    passesLeft: {
      immediate: true,
      handler(count) {
        if (!this.loading) {
          this.$emit('passes-left', count);
        }
      },
    },
  },
  created() {
    this.load();
    this.$notificationManager.on('class-pass-changed', this.load);
  },
  destroyed() {
    this.$notificationManager.off('class-pass-changed', this.load);
  },
  methods: {
    load() {
      const api = this.$apiService;
      this.loading = this.available.length === 0 && this.passes.length === 0;
      Promise.all([
        api.loadAllPages(api.availableWrestlerClassPasses(this.wrestlerId), 'class_passes'),
        axios.get(`${api.wrestlerClassPasses(this.wrestlerId)}?per_page=10`),
        // Totals only (TotalCount header), for the activity link's label
        axios.get(`${api.wrestlerClassPasses(this.wrestlerId)}?status=used&per_page=1`),
        axios.get(`${api.wrestlerClassPasses(this.wrestlerId)}?status=expired&per_page=1`),
      ])
        .then(([available, response, used, expired]) => {
          this.available = available;
          this.passes = response.data.class_passes;
          this.usedTotal = Number(used.headers.totalcount) || 0;
          this.expiredTotal = Number(expired.headers.totalcount) || 0;
          this.nextActivityUrl = api.nextPageUrl(response.headers);
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          this.error = true;
          this.errorMessage = `Error loading class passes ${error.toString()}`;
        });
    },
    loadActivity(url) {
      axios.get(url)
        .then((response) => {
          this.passes = this.passes.concat(response.data.class_passes);
          this.nextActivityUrl = this.$apiService.nextPageUrl(response.headers);
        })
        .catch((error) => {
          this.error = true;
          this.errorMessage = `Error loading class passes ${error.toString()}`;
        });
    },
    // A drop in / trial session's purchases: passes left, how they were used,
    // and what was paid
    purchaseSource(registrations) {
      const latest = registrations[0];
      const counts = registrations.map((r) => r.class_passes).filter(Boolean);
      const available = _.sumBy(counts, 'available');
      const used = _.sumBy(counts, 'used');
      const expired = _.sumBy(counts, 'expired');
      const lastUsed = _.max(_.compact(counts.map((c) => c.last_used_at)));
      const trial = latest.paid_session.session_type === 'trial';
      const ids = registrations.map((r) => r.id);

      let detail;
      if (available > 0) {
        detail = this.expiryText(this.available.filter((pass) => ids.includes(pass.registration_id)));
      } else if (used > 0) {
        const total = used + expired;
        if (trial) {
          detail = `Trial used ${this.shortDate(lastUsed)}`;
        } else if (total === 1) {
          detail = `Used ${this.shortDate(lastUsed)}`;
        } else {
          detail = `${expired > 0 ? `${used} of ${total}` : `All ${used}`} ${total === 1 ? 'pass' : 'passes'} used · last ${this.shortDate(lastUsed)}`;
        }
      } else if (expired > 0) {
        detail = 'Expired unused';
      } else {
        detail = 'No passes issued yet';
      }

      const paid = _.sumBy(registrations, (r) => _.sumBy(_.get(r, 'payment_plan.installments', []), (i) => i.amount_paid || 0));
      const paidBy = _.get(latest, 'payment_plan.billed_to.display_name');
      const amount = this.hidePayments ? '' : `, ${this.formatAsCurrency(paid)}`;
      const bought = registrations.length > 1
        ? `${registrations.length} purchases${amount} · last ${this.shortDate(latest.created_at)}`
        : `Bought ${this.shortDate(latest.created_at)}${this.hidePayments ? '' : ` · ${this.formatAsCurrency(paid)}`}`;
      const overdue = !this.hidePayments && registrations.some((r) => r.status === 'overdue');
      return {
        key: `purchase-${latest.paid_session.id}`,
        name: latest.paid_session.name,
        count: available,
        of: null,
        detail,
        purchase: _.compact([bought, paidBy && !this.hidePayments ? `Paid by ${paidBy}` : null]).join(' · '),
        badge: overdue ? { text: 'Overdue', tone: 'danger' } : null,
        sortAt: lastUsed || latest.created_at,
      };
    },
    expiryText(passes) {
      const soonest = _.min(_.compact(passes.map((p) => p.expires_at)));
      if (!soonest) {
        return 'No expiry';
      }
      return `${passes.length > 1 ? 'Next expires' : 'Expires'} ${this.shortDate(soonest)}`;
    },
    sourceKey(pass) {
      if (pass.billing_subscription) {
        return `sub-${pass.billing_subscription.id}`;
      }
      return pass.paid_session ? `session-${pass.paid_session.id}` : 'other';
    },
    sourceName(pass) {
      if (pass.billing_subscription) {
        return pass.billing_subscription.name || 'Subscription';
      }
      return pass.paid_session ? pass.paid_session.name : 'Class pass';
    },
    weeklyQuantity(subscriptionId) {
      const subscription = _.find(this.subscriptions, ['id', subscriptionId]);
      const plan = _.find(_.get(subscription, 'billing_plans', []), (p) => p.class_pass_quantity > 0);
      return plan ? plan.class_pass_quantity : null;
    },
    isExpired(pass) {
      return pass.status === 'expired' || (pass.status === 'available' && pass.expires_at && moment(pass.expires_at).isBefore(moment()));
    },
    shortDate(value) {
      const date = moment(value);
      return date.year() === moment().year() ? date.format('MMM D') : date.format('MMM D, YYYY');
    },
  },
};
</script>
